import { ELButton, ELRadioButton } from '@davincihealthcare/elty-design-system-react';
import { ChevronUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useState } from 'react';

export type SelectRadioButtonProps<T> = {
  label: string;
  options: {
    value: T | undefined;
    label: string;
  }[];
  selected: T | undefined;
  setSelected: (filter: T | undefined) => void;
} & (
  | {
      primaryCTA: {
        label: string;
        onClick: () => void;
      };
      secondaryCTA: {
        label: string;
        onClick: () => void;
      };
      floating: boolean;
    }
  | { floating?: undefined; primaryCTA?: undefined; secondaryCTA?: undefined }
);

const SelectRadioButton = <T extends string | undefined>({
  label,
  options,
  selected,
  setSelected,
  primaryCTA,
  secondaryCTA,
  floating,
}: SelectRadioButtonProps<T>) => {
  const [isFiltersCollapsed, setFiltersCollapsed] = useState(false);
  return (
    <div
      className={clsx(
        floating ? 'absolute w-[365px] shadow-xl' : 'relative w-full',
        'z-10 mt-1 flex flex-col gap-3 bg-neutral-surface p-3',
      )}
    >
      <div className="flex items-center justify-between">
        <h1 className="py-1 text-base font-semibold text-primary">{label}</h1>
        {!floating && (
          <ChevronUpIcon
            className={clsx(isFiltersCollapsed && 'rotate-180', 'w-4 cursor-pointer')}
            onClick={() => setFiltersCollapsed(prevValue => !prevValue)}
          />
        )}
      </div>
      <div className={clsx(isFiltersCollapsed && 'hidden h-0', 'flex flex-col gap-3')}>
        {options.map((option, index) => (
          <React.Fragment key={option.label}>
            <ELRadioButton
              name={`radio_button_${index}`}
              aria-label={option.label}
              label={option.label}
              checked={selected === option.value}
              onChange={() => setSelected(option.value)}
            />
            {index !== options.length - 1 && <hr />}
          </React.Fragment>
        ))}
      </div>
      {floating ? (
        <div className="flex flex-row items-center justify-end gap-3">
          <ELButton size="small" variant="text" color="primary" label={secondaryCTA.label} onClick={secondaryCTA.onClick} />
          <ELButton size="small" variant="filled" color="primary" label={primaryCTA.label} onClick={primaryCTA.onClick} />
        </div>
      ) : (
        <hr className="w-full" />
      )}
    </div>
  );
};

export default SelectRadioButton;
